// Libraries
import React from 'react';

// Supermove
import {DataLoader, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useModal, useNavigationDOM, useQuery, useResponsive} from '@supermove/hooks';
import {StageModel} from '@supermove/models';
import {colors} from '@supermove/styles';

// App
import DropdownButton from '@shared/design/components/Button/DropdownButton';
import TertiaryButton from '@shared/design/components/Button/TertiaryButton';
import Text from '@shared/design/components/Text';
import StagePageContent from '@shared/modules/Sales/components/StagePageContent';
import ProjectSettingsPage from 'modules/Organization/Settings/Project/ProjectSettingsPage';

const ContentContainer = Styled.View`
  padding-horizontal: 24px;
  width: 832px;
`;

const Row = Styled.View`
  flex-direction: row;
`;

const ViewSalesStagePageContent = ({stage, refetch}: {stage: StageModel; refetch: () => void}) => {
  const {navigator} = useNavigationDOM();
  const responsive = useResponsive();
  const isArchived = !!stage?.archivedAt;
  const archiveStageModal = useModal({name: 'Archive Sales Stage Modal'});
  const restoreStageModal = useModal({name: 'Restore Sales Stage Modal'});
  const dropdownButtonActions = [
    {
      text: isArchived ? 'Restore' : 'Archive',
      color: isArchived ? colors.gray.primary : colors.red.warning,
      onPress: () => {
        if (isArchived) {
          restoreStageModal.handleOpen();
        } else {
          archiveStageModal.handleOpen();
        }
      },
    },
  ];

  const actionElement = responsive.desktop ? (
    <DropdownButton text={'Actions'} actions={dropdownButtonActions} />
  ) : (
    <DropdownButton
      text={'Actions'}
      isResponsive
      ButtonComponent={TertiaryButton}
      actions={dropdownButtonActions}
    />
  );

  return (
    <ProjectSettingsPage
      title={''}
      description={''}
      breadcrumbs={[
        {name: 'Settings', onPress: () => navigator.push('/settings')},
        {name: 'Projects', onPress: () => navigator.push('/settings/projects/stages')},
        {name: 'Sales Stage'},
      ]}
      isCompanySettings
    >
      <ContentContainer>
        <Space height={responsive.desktop ? 24 : 16} />
        <Row style={{zIndex: 99999}}>
          <Text.PageHeading>{stage.name}</Text.PageHeading>
          <Space flex={1} />
          {actionElement}
        </Row>
        {!!stage.description && (
          <React.Fragment>
            <Space height={12} />
            <Text.Body style={{color: colors.gray.secondary}}>{stage.description}</Text.Body>
          </React.Fragment>
        )}
        <Space height={24} />
        <StagePageContent
          stage={stage}
          refetch={refetch}
          archiveStageModal={archiveStageModal}
          restoreStageModal={restoreStageModal}
        />
      </ContentContainer>
    </ProjectSettingsPage>
  );
};

const ViewSalesStagePage = () => {
  const {params} = useNavigationDOM();
  const {loading, data, refetch} = useQuery(ViewSalesStagePage.query, {
    fetchPolicy: 'cache-and-network',
    variables: {
      stageUuid: params.stageUuid,
    },
  });

  return (
    <DataLoader loading={loading} data={data}>
      {({loadedData}) => {
        return <ViewSalesStagePageContent stage={loadedData.stage} refetch={refetch} />;
      }}
    </DataLoader>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ViewSalesStagePage.query = gql`
  ${StagePageContent.fragment}
  query ViewSalesStagePage($stageUuid: String!) {
    ${gql.query}
    stage(uuid: $stageUuid) {
      id
      name
      description
      archivedAt
      ...StagePageContent
    }
  }
`;

export default ViewSalesStagePage;
